import React, { useState, useEffect } from "react";
import { SelectPicker } from "rsuite";
import { Supplier, SupplierSelectProps } from "../my-type";
import { getSuppliersByTeamId } from "../service/SupplierService";

/**
 * A dropdown component for selecting a Supplier associated with a team.
 * Fetches suppliers from the API based on the provided teamId.
 */
export default function SupplierSelect({
  teamId,
  value,
  onChange,
  placeholder = "請選擇供應商",
  disabled = false,
  block = true
}: SupplierSelectProps) {
  const [suppliers, setSuppliers] = useState<Supplier[]>([]);
  const [loading, setLoading] = useState<boolean>(false);

  useEffect(() => {
    let cancelled = false;

    const fetchSuppliers = async () => {
      if (!teamId) {
        setSuppliers([]);
        return;
      }

      setLoading(true);

      try {
        const supplierList = await getSuppliersByTeamId(teamId);
        if (cancelled) {
          return;
        }

        if (supplierList) {
          setSuppliers(supplierList);
        } else {
          setSuppliers([]);
        }
      } catch (err) {
        if (cancelled) {
          return;
        }
        console.error("Error loading suppliers:", err);
        setSuppliers([]);
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };

    fetchSuppliers();

    return () => {
      cancelled = true;
    };
  }, [teamId]);

  useEffect(() => {
    if (suppliers.length === 1 && !value) {
      onChange(suppliers[0].id);
    }
  }, [suppliers, value, onChange]);

  const supplierOptions = suppliers.map((supplier) => ({
    label: supplier.name,
    value: supplier.id
  }));

  return (
    <SelectPicker
      data={supplierOptions}
      value={value}
      onChange={onChange}
      placeholder={placeholder}
      disabled={disabled || loading}
      loading={loading}
      block={block}
      searchable
      cleanable
      style={{ width: block ? "100%" : undefined }}
    />
  );
}
